/****************************************************
 * Module Name:  convertPacket.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Box, Button, Tooltip } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import renderCellExpand from "../../common/dataGridCellExpand";
import Utility from "../../../utility/utility";
import { useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { SiConvertio } from "react-icons/si";
import PacketHistoryModal from "../../common/packetHistoryModal";
import AddConvertToStoneModal from "../../manufactureMaster/addConvertToStoneModal";
import Constants from "../../../utility/constants";
import CommonStoneSelection from "../../common/commonStoneSelection";
import NoPermission from "../../common/noPermission";
import EditIcon from '@mui/icons-material/Edit';
import CommonSnackbar from "../../common/commonSnackBar";
import KapanHistoryModal from "./kapanHistoryModal";
import PacketProxy from "../../../utility/proxy/manager/manufacturingProxy/packetProxy";
import MixStoneBucketModal from "../../operations/mixStoneBucketModal";
import PageTitle from "../../common/pageTitle";

const ConvertPacket = () => {
    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();
    const convertToStonePermission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_CONVERTTOSTONE);

    const addToBucketPermission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_CONVERTTOBUCKET);

    const [listData, setListData] = useState([]);
    const [loader, setLoader] = useState(false);
    const [selectionModel, setSelectionModel] = useState([]);
    const [sourceData, setSourceData] = useState({});

    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
    const [showAddConvertToStoneModal, setShowAddConvertToStoneModal] = useState(false);
    const [showAddConvertToBucketModal, setShowAddConvertToBucketModal] = useState(false);

    const [loadingButton, setLoadingButton] = useState(false);
    const [packetId, setPacketId] = useState("");
    const [kapanId, setKapanId] = useState("");

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [filterValues, setFilterValues] = useState("");

    const [totalPacket, setTotalPacket] = useState(0);
    const [totalWeight, setTotalWeight] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [avgPrice, setAvgPrice] = useState(0);

    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);

    useEffect(() => {
        PageTitle("MANUFACTURING_OPERATION_CONVERTPACKET");
        if (Utility.isReadAllowed(convertToStonePermission) || Utility.isReadAllowed(addToBucketPermission)) {
            handleSearch();
        }
    }, []);

    useEffect(() => {
        if (selectionModel.length === 0)
            getSummaryReport(listData);
        else
            getSummaryReport(listData.filter(iData => selectionModel.includes(iData.kapanid_packetid)));
    }, [selectionModel]);

    const handleSearch = () => {
        setLoader(true);
        PacketProxy.GetAllMyPacketWithEvaluation((response) => {
            if (response) {
                const uniqueFilterValues = {
                    shape: Utility.getDistinctOptions(response, "shape"),
                    color: Utility.getDistinctOptions(response, "color"),
                    clarity: Utility.getDistinctOptions(response, "clarity"),
                    cut: Utility.getDistinctOptions(response, "cut"),
                    pol: Utility.getDistinctOptions(response, "pol"),
                    sym: Utility.getDistinctOptions(response, "sym"),
                    flo: Utility.getDistinctOptions(response, "flo"),
                }
                setFilterValues(uniqueFilterValues);
                // Add Embedded Key - Kapan Id & Packet Id
                response = response.map(data => ({ ...data, kapanid_packetid: data.kapanid + "_" + data.packetid }));

                setListData(response.sort((a, b) => a.packetid.localeCompare(b.packetid, undefined, { numeric: true })));
                setLoader(false);
                getSummaryReport(response);
            } else {
                setLoader(false);
            }
        });
    };

    const getSummaryReport = (data) => {
        if (!Array.isArray(data)) return;
        let totalPackets = data.length;
        let weight = 0;
        let price = 0;
        data.forEach((item) => {
            weight += item.size;
            price += item.size * item.price;
        });
        let avgprice = price / weight;

        setTotalPacket(totalPackets);
        setTotalPrice(price);
        setTotalWeight(weight);
        setAvgPrice(avgprice);
    }

    const handleValidation = (convertStone) => {

        let flag = true;
        listData.forEach(iData => {
            if (flag && selectionModel.includes(iData.kapanid_packetid)) {

                if (iData.packetsize !== iData.size) {
                    flag = false;
                    setShowSnackbar(true);
                    setSnackbarVariant("error");
                    setSnackbarMessage("Packet Weight and Size do not match!! Please update/add latest evaluation for the packet.");
                }
            }
        })

        if (flag)
            convertStone ?
                setShowAddConvertToStoneModal(true) :
                setShowAddConvertToBucketModal(true);
    };

    const packetStone = (data) => {
        setLoadingButton(true)
        // Creating new issue, and once issue is created on call refresh the list of current issue
        PacketProxy.AddConvertToStone(data, Constants.loggedInUser.locationid, (response) => {
            if (response) {
                setLoadingButton(false)
                setShowAddConvertToStoneModal(false)
                setShowSnackbar(true);
                setSnackbarMessage("Packet converted to stone successfully.")
                setSnackbarVariant("success");
                handleSearch()
            } else {
                setLoadingButton(false)
                setShowAddConvertToStoneModal(false)
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage)
                setSnackbarVariant("error");
            }
        });
    };

    const packetBucket = (data) => {
        setLoadingButton(true)
        // Creating new issue, and once issue is created on call refresh the list of current issue
        PacketProxy.AddConvertToBucket(data, Constants.loggedInUser.locationid, (response) => {
            if (response.status === "Success") {
                setLoadingButton(false)
                setShowAddConvertToBucketModal(false)
                setShowSnackbar(true);
                setSnackbarMessage("Packet converted to mix successfully.")
                setSnackbarVariant("success");
                handleSearch()
            } else {
                setLoadingButton(false)
                setShowAddConvertToBucketModal(false)
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage)
                setSnackbarVariant("error");
            }
        });
    };

    const handleSelection = (data) => {
        let lstKapanId_PacketId = [];
        data.forEach(iData => {
            if (iData.includes("_")) {
                lstKapanId_PacketId.push(iData);
            } else {
                let lstRecords = listData.filter(iRow => iRow.packetid === iData);
                if (lstRecords.length > 0)
                    lstRecords.forEach(iRow => lstKapanId_PacketId.push(iRow.kapanid_packetid));
            }
        });
        setSelectionModel([...selectionModel, ...lstKapanId_PacketId])
    }

    const handleProcessRowUpdateError = (error) => {
        // console.error(error);
    };

    const getChangedFields = (newRow, oldRow) => {
        const changedFields = [];

        Object.keys(newRow).forEach(field => {
            if (newRow[field] !== oldRow[field]) {
                changedFields.push({
                    id: newRow.kapanid_packetid,
                    field: field,
                    value: newRow[field]
                });
            }
        });

        return changedFields;
    };

    const processRowUpdate = (newRow, oldRow) => {
        const changedFeilds = getChangedFields(newRow, oldRow);
        if (changedFeilds.length > 0) {
            const { id, field, value } = changedFeilds[0];

            const updatedListData = listData.map((iData) => {
                if (iData.kapanid_packetid === id && value) {
                    if (field === "price") {
                        return {
                            ...iData,
                            [field]: parseFloat(value.toFixed(2)),
                            total: parseFloat((value * iData.size).toFixed(2)),
                            discount: parseFloat((((iData.rapnetprice - value) / iData.rapnetprice) * -100).toFixed(2))
                        }
                    } else {
                        return {
                            ...iData,
                            [field]: parseFloat(value.toFixed(2)),
                            price: parseFloat((value / iData.size).toFixed(2)),
                            discount: parseFloat((((iData.rapnetprice - (value / iData.size)) / iData.rapnetprice) * -100).toFixed(2))
                        }
                    }
                }
                return iData;
            })

            setListData(updatedListData);
            const updatedRow = updatedListData.find(row => row.kapanid_packetid === id);

            return updatedRow;
        } else {
            // No changes detected, return the newRow to trigger exit edit mode
            return { ...newRow };
        }
    };

    const initialColumns = () => [
        {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid)
                        setPacketId(params.row.packetid)
                        setShowPacketDetailModal(true)
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        // {
        //     field: "bucketid",
        //     headerName: "BUCKETID",
        //     headerAlign: "center",
        //     align: "center",
        //     type: "number",
        //     editable: true,
        //     width: 120,
        //     valueFormatter: (params) => params.value,
        //     renderCell: (params) => (
        //         <div style={{ display: 'flex', alignItems: 'center' }}>
        //             <span style={{ marginRight: '5px', marginLeft: '10px' }}>
        //                 {params.value}
        //             </span>
        //             <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />
        //         </div>
        //     ),
        //     cellClassName: (params) => 'lavenderCell'
        // },
        {
            field: "packetsize",
            headerName: "PKT CARAT",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "number",
            valueFormatter: (params) => !params.value ? 0 : (params.value).toFixed(2),
            renderCell: (params) => !params.value ? 0 : (params.value).toFixed(2),
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 40,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => Constants.STONE_TYPE[params.value],
            renderCell: (params) => Constants.STONE_TYPE[params.value],
        },
        {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            width: 80,
            renderCell: renderCellExpand,
        },
        {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            width: 65,
            renderCell: renderCellExpand,
        },
        {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            width: 75,
            renderCell: renderCellExpand,
        },
        {
            field: "rapnetprice",
            headerName: "RAP",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.rapnetprice)
        },
        {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 50,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.discount)
        },
        {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            width: 100,
            type: "number",
            editable: true,
            valueFormatter: (params) => !params.value ? 0 : (params.value).toFixed(2),
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value}
                    </span>
                    <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />
                </div>
            ),
            cellClassName: (params) => 'lavenderCell'
        },
        {
            field: "total",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "right",
            width: 100,
            type: "number",
            editable: true,
            valueFormatter: (params) => !params.value ? 0 : (params.value).toFixed(2),
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value}
                    </span>
                    <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />
                </div>
            ),
            cellClassName: (params) => 'lavenderCell'
        },
        {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            width: 50,
            renderCell: renderCellExpand,
        },
        {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            width: 40,
            renderCell: renderCellExpand,
        },
        {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            width: 60,
            renderCell: renderCellExpand,
        },
        // {
        //     field: "user",
        //     headerName: "USER",
        //     headerAlign: "center",
        //     align: "center",
        //     width: 250,
        //     type: "singleSelect",
        //     valueOptions: filterValues ? filterValues.user : [],
        //     filterOperators: Utility.combinedFilters(),
        //     valueGetter: (params) => params.row.userid + " | " + params.row.username,
        //     renderCell: renderCellExpand
        // },
        // {
        //     field: "department",
        //     headerName: "DEPARTMENT",
        //     headerAlign: "center",
        //     align: "center",
        //     width: 220,
        //     type: "singleSelect",
        //     valueOptions: filterValues ? filterValues.department : [],
        //     filterOperators: Utility.combinedFilters(),
        //     valueGetter: (params) => params.row.department_id + " | " + params.row.department_name,
        //     renderCell: renderCellExpand
        // },
        // {
        //     field: "nextdepartmentheadid",
        //     headerName: "ISSUED DEPARTMENT HEAD",
        //     headerAlign: "center",
        //     align: "center",
        //     width: 230,
        //     renderCell: (params) => (
        //         params.row.next_department_head_id ?
        //             <Box sx={{ minHeight: "auto", mt: 1 }}>
        //                 <Chip
        //                     avatar={<Avatar>{params.value}</Avatar>}
        //                     label={params.row.nextdepartmenthead}
        //                     sx={{ margin: "0px 10px 10px 0px" }}
        //                 />
        //             </Box>
        //             : '-'
        //     ),
        //     disableExport: true,
        // },
        // {
        //     field: "nextdepartmentid",
        //     headerName: "ISSUED DEPARTMENT NAME",
        //     headerAlign: "center",
        //     align: "center",
        //     width: 230,
        //     renderCell: (params) => (
        //         params.row.next_department_id ?
        //             <Box sx={{ minHeight: "auto", mt: 1 }}>
        //                 <Chip
        //                     avatar={<Avatar>{params.value}</Avatar>}
        //                     label={params.row.nextdepartmentname}
        //                     sx={{ margin: "0px 10px 10px 0px" }}
        //                 />
        //             </Box> : '-'
        //     ),
        //     disableExport: true,
        // },
    ];
    const columns = initialColumns();

    return (
        <div>

            <CommonSnackbar
                show={showSnackbar}
                message={snackbarMessage}
                variant={snackbarVariant}
                autoHide={Constants.SnackBarCloseTimer}
                title={snackbarVariant === "success" ? "Success" : "Error"}
                handleClose={() => setShowSnackbar(false)}
            />

            {showPacketDetailModal ? (
                <PacketHistoryModal
                    show={showPacketDetailModal}
                    packetId={packetId}
                    kapanId={kapanId}
                    close={() => setShowPacketDetailModal(false)}
                />
            ) : null}

            {showAddConvertToStoneModal ? (
                <AddConvertToStoneModal
                    show={true}
                    handleClose={() => setShowAddConvertToStoneModal(false)}
                    handleSave={packetStone}
                    handleValidation={() => { return true }}
                    loading={loadingButton}
                    sourceData={sourceData}
                />
            ) : null}

            {showAddConvertToBucketModal ? (
                <MixStoneBucketModal
                    show={showAddConvertToBucketModal}
                    setShow={setShowAddConvertToBucketModal}
                    handleSave={packetBucket}
                    loading={loadingButton}
                    sourceData={sourceData}
                    convertPacket={true}
                />
            ) : null}

            {showKapanDetailModal ? (
                <KapanHistoryModal
                    show={showKapanDetailModal}
                    kapanId={kapanId}
                    close={() => setShowKapanDetailModal(false)}
                />
            ) : null}

            {!Utility.isReadAllowed(convertToStonePermission) && !Utility.isReadAllowed(addToBucketPermission) ? <NoPermission /> : (
                <Box
                    sx={{
                        marginTop: "16px",
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "100%",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .lavenderCell": {
                            color: "#080808",
                            backgroundColor: "#D8BFD8",
                        },
                        height: "84vh",
                    }}
                >
                    <DataGridPro
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "4px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "12px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "18px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                        }}
                        disableRowSelectionOnClick={true}
                        density="compact"
                        loading={loader}
                        slots={{ toolbar: GridToolbar }}
                        rows={listData}
                        columns={columns}
                        getRowId={(row) => row.kapanid_packetid}
                        checkboxSelection={true}
                        onRowSelectionModelChange={(newSelectionModel) => {
                            const selectedIDs = new Set(newSelectionModel);
                            const selectedRowData = listData.filter((row) => selectedIDs.has(row.kapanid_packetid))
                            setSelectionModel(newSelectionModel);
                            setSourceData(selectedRowData)
                        }}
                        rowSelectionModel={selectionModel}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        apiRef={apiRef}
                        onCellClick={handleCellClick}
                        editMode="cell"
                    />

                    <Box sx={{
                        position: "absolute",
                        left: "0.4rem",
                        top: "1px"
                    }}>
                        <CommonStoneSelection title={"Enter Multiple 'KapanId_PacketId'"} handleSelection={handleSelection} buttonDisabled={listData.length === 0} />
                    </Box>

                    <Box
                        sx={{
                            position: "absolute",
                            top: "11px",
                            left: "462px",
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '36rem',
                            color: "black",
                            fontWeight: 'normal',
                            fontSize: Constants.defaultTableFontSize
                        }}
                    ><p style={{ margin: 0 }}>
                            <span>Total Packet:</span> <b>{totalPacket.toFixed(2)}</b> &nbsp;&nbsp;|&nbsp;&nbsp;
                            <span>Total Carats: </span> <b>{totalWeight.toFixed(2)}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                            <span>Total Price: </span> <b>{totalPrice.toFixed(2)}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                            <span>Avg Price: </span> <b>{isNaN(avgPrice) ? "0.00" : avgPrice.toFixed(2)}</b></p>
                    </Box>

                    <Box
                        sx={{
                            position: "absolute",
                            top: 6,
                            right: "260px",
                            display: "flex",
                            alignItems: "center",
                            "& .Mui-disabled": {
                                backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                color: "rgba(0, 0, 0, 0.26) !important"
                            }
                        }}
                    >
                        {loader ? (
                            <CircularProgress size="1rem" />
                        ) : (
                            <Tooltip title="Reload List">
                                <RefreshOutlinedIcon
                                    onClick={() => handleSearch()}
                                    color="primary"
                                    fontSize="medium"
                                    sx={{ cursor: "pointer" }}
                                />
                            </Tooltip>
                        )}

                        {Utility.isUpdateAllowed(addToBucketPermission) ? (
                            <Tooltip title="Add To Bucket">
                                <Button
                                    component="label"
                                    color="info"
                                    variant="contained"
                                    size="small"
                                    sx={{ ml: "5px" }}
                                    startIcon={<SiConvertio sx={{ size: "12px" }} />}
                                    disabled={selectionModel.length < 1}
                                    onClick={() => {
                                        handleValidation(false)
                                    }}
                                >
                                    Add To Bucket
                                </Button>
                            </Tooltip>
                        ) : null}

                        {Utility.isUpdateAllowed(convertToStonePermission) ? (
                            <Tooltip title="Convert To Stone">
                                <Button
                                    component="label"
                                    color="info"
                                    variant="contained"
                                    size="small"
                                    sx={{ ml: "5px" }}
                                    startIcon={<SiConvertio sx={{ size: "12px" }} />}
                                    disabled={selectionModel.length < 1}
                                    onClick={() => handleValidation(true)}
                                >
                                    Convert To Stone
                                </Button>
                            </Tooltip>
                        ) : null}

                    </Box>

                </Box>
            )}
        </div >
    );
};

export default ConvertPacket;
